import { ReactElement, useEffect, useMemo, useState } from 'react';

import styled from 'styled-components';

import { Button } from '@components/button';
import { Card } from '@components/card';
import { SortableList } from '@components/drag-and-drop/sortable-list';
import { createForm, useCreateFormStore } from '@components/form';
import { FormError } from '@components/form-error';
import { IconButton } from '@components/icon-button';
import { IconFancyContainer } from '@components/icon-fancy-container';
import { IconProps } from '@components/icons';
import { IconAddPlus } from '@components/icons/add-plus';
import { IconCloudSync } from '@components/icons/cloud-sync';
import { IconCloudUnsync } from '@components/icons/cloud-unsync';
import { IconDate } from '@components/icons/date';
import { IconDrag } from '@components/icons/drag';
import { IconEdit } from '@components/icons/edit';
import { IconFilter } from '@components/icons/filter';
import { IconIntegration } from '@components/icons/integration';
import { IconList } from '@components/icons/list';
import { IconRemove } from '@components/icons/remove';
import { IconRestart } from '@components/icons/restart';
import { IconReturn } from '@components/icons/return';
import { IconUrl } from '@components/icons/url';
import { Spinner } from '@components/loading-spinner';
import { forceHideSidebar, Sidebar, useSidebar } from '@components/sidebar';
import { SquareImage } from '@components/square-image';
import { Stack } from '@components/stack';
import { Switch } from '@components/switch';
import { Text } from '@components/text';
import { useScopedTranslate } from '@locale/translate';
import { theme } from '@styles/theme';
import { pxToRem } from '@utils/converters';
import { abbreviateDate } from '@utils/date';
import { uuid } from '@utils/uuid';

import { useList, useCreateList, useEditList, usePatchSyncList, useSyncList } from './list-hooks';
import { useSyncListSidebar } from './sync-list-sidebar';

import { ListsScopeProvider } from '../integrations-hooks';
import type { CreateListData, List, SyncListType } from '../integrations-types';

export type ListOption = {
  value: string;
  label: string;
  shortId: string;
  isNew?: boolean;
};

export type ListSidebarProps = {
  listId?: string;
  baseUrl?: string; // For Marketplace: baseUrl for sync list path building
  integrationId?: string; // For Marketplace: pre-selected integration
  templateId?: string; // For Marketplace: template context for API calls
};

const Form = createForm<{ name: string; source: string }>();
const OptionForm = createForm<ListOption>();

export const ListSidebar = ({ listId, baseUrl, integrationId, templateId }: ListSidebarProps) => {
  return (
    <ListsScopeProvider templateId={templateId}>
      <ListSidebarContent
        listId={listId}
        baseUrl={baseUrl}
        integrationId={integrationId}
        templateId={templateId}
      />
    </ListsScopeProvider>
  );
};

const ListSidebarContent = ({ listId, baseUrl, integrationId, templateId }: ListSidebarProps) => {
  const { t } = useScopedTranslate(locale);
  const formStore = useCreateFormStore();
  const isFormDirty = formStore.useIsDirty();

  const listQuery = useList(listId ?? '');
  const createMutation = useCreateList();
  const editMutation = useEditList();
  const patchSyncMutation = usePatchSyncList();
  const syncMutation = useSyncList();
  const showSyncListSidebar = useSyncListSidebar();

  const list = listQuery.data;
  const isEdit = Boolean(listId);
  const isLoading =
    (isEdit && listQuery.isLoading) || createMutation.isLoading || editMutation.isLoading;

  const [options, setOptions] = useState<ListOption[]>([]);
  const [initialOptions, setInitialOptions] = useState<ListOption[]>([]);
  const [newOptionShortId, setNewOptionShortId] = useState<string | null>(null);
  const [noOptionsError, setNoOptionsError] = useState(false);
  const listSyncData = list?.sync;
  const isSyncedList = Boolean(listSyncData);

  useEffect(() => {
    if (list?.options) {
      setOptions(list.options);
      setInitialOptions(list.options);
    }
  }, [list?.options]);

  const hasNewOptionEditing = newOptionShortId !== null;

  const areOptionsDirty = useMemo(() => {
    if (options.length !== initialOptions.length) return true;
    return options.some((option, index) => {
      const initial = initialOptions[index];
      if (!initial) return true;
      return option.value !== initial.value || option.label !== initial.label;
    });
  }, [options, initialOptions]);

  const isDirty = isFormDirty || areOptionsDirty;

  const isUuid = (value: unknown) => {
    return (
      typeof value === 'string' &&
      /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(value)
    );
  };

  const handleSave = async (data: { name: string; source: string }) => {
    if (isSyncedList) return;

    if (options.length === 0) {
      setNoOptionsError(true);
      return;
    }

    const newOptions = options.map((option) => {
      const { shortId, isNew, ...rest } = option;
      return isUuid(shortId) ? rest : { ...rest, shortId };
    });

    const saveData: CreateListData = { ...data, options: newOptions };

    if (listId) {
      await editMutation.mutateAsync({ id: listId, data: saveData });
    } else {
      await createMutation.mutateAsync(saveData);
    }
    forceHideSidebar();
  };

  const handlePatchSyncList = (id: string, data: Partial<SyncListType>) => {
    patchSyncMutation.mutate({ id, data });
  };

  const handleSyncList = (id: string) => {
    syncMutation.mutate(id);
  };

  const handleEditSyncList = (editList: List) => {
    showSyncListSidebar({ list: editList, baseUrl, integrationId, templateId });
  };

  const changeOrder = (activeIndex: number, overIndex: number) => {
    const next = [...options];
    const [moved] = next.splice(activeIndex, 1);
    next.splice(overIndex, 0, moved);
    setOptions(next);
  };

  const isDuplicateValue = (value: string, shortId: string) => {
    return options.some((option) => option.value === value && option.shortId !== shortId);
  };

  const editOption = (updated: ListOption) => {
    setOptions((prev) => prev.map((opt) => (opt.shortId === updated.shortId ? updated : opt)));
    setNoOptionsError(false);
    if (updated.shortId === newOptionShortId) {
      setNewOptionShortId(null);
    }
  };

  const removeOption = (target: ListOption) => {
    if (target.shortId === newOptionShortId) {
      setNewOptionShortId(null);
    }
    setOptions((prev) => prev.filter((opt) => opt.shortId !== target.shortId));
  };

  const handleAddOption = () => {
    const shortId = uuid();
    const newOption: ListOption = {
      value: '',
      label: '',
      shortId,
      isNew: true,
    };
    setOptions((prev) => [...prev, newOption]);
    setNewOptionShortId(shortId);
    setNoOptionsError(false);
  };

  if (listId && listQuery.isLoading) {
    return (
      <Sidebar>
        <Sidebar.SimpleHeader title={t('titleEdit')} icon={IconList} iconColor="elementBlue100" />
        <Sidebar.Content>
          <Spinner />
        </Sidebar.Content>
      </Sidebar>
    );
  }

  return (
    <Sidebar>
      <Sidebar.SimpleHeader
        title={isEdit ? t('titleEdit') : t('title')}
        icon={IconList}
        iconColor="elementBlue100"
      />
      <Sidebar.Content>
        <Stack space={6}>
          <Form store={formStore} onSubmit={handleSave}>
            <Container>
              <Text text={t('details')} font="semibold14" />
              <div>
                <Stack space={4}>
                  <Form.TextField
                    id="name"
                    key={`list-name-${list?.shortId}`}
                    label={t('name')}
                    defaultValue={list?.name ?? ''}
                    validators={['required']}
                    isDisabled={isSyncedList}
                    maxChars={255}
                  />
                  {!isSyncedList && (
                    <Form.TextField
                      id="source"
                      label={t('source')}
                      defaultValue={list?.source ?? ''}
                    />
                  )}
                  {isSyncedList && listSyncData && (
                    <SyncedListDetails
                      syncData={listSyncData}
                      onPatchSyncList={handlePatchSyncList}
                    />
                  )}
                </Stack>
              </div>
            </Container>
            <Form.Actions portal="list-form-actions">
              <Sidebar.Footer>
                <Stack
                  space={4}
                  orientation="horizontal"
                  justify={isSyncedList ? 'space-between' : 'end'}
                >
                  {isSyncedList && listSyncData && list && (
                    <Stack space={2} orientation="horizontal" align="center">
                      <Button
                        variant="secondary"
                        text={t('edit')}
                        iconLeft={<IconEdit />}
                        onClick={() => handleEditSyncList(list)}
                      />
                      <Button
                        variant="secondary"
                        text={t('resync')}
                        iconLeft={<IconRestart />}
                        onClick={() => handleSyncList(listSyncData.shortId)}
                      />
                    </Stack>
                  )}
                  <Stack space={4} orientation="horizontal" justify="end">
                    <Button variant="secondary" text={t('cancel')} onClick={forceHideSidebar} />
                    {!isSyncedList && (
                      <Form.Submit
                        variant="primary-blue"
                        type="submit"
                        text={isEdit ? t('save') : t('title')}
                        isDisabled={hasNewOptionEditing || (isEdit && !isDirty) || isLoading}
                      />
                    )}
                  </Stack>
                </Stack>
              </Sidebar.Footer>
            </Form.Actions>
          </Form>
          <Sidebar.Separator />
          <Container>
            <Text text={t('options')} font="semibold14" />
            <Stack space={2}>
              {options.length === 0 ? (
                <Stack space={1}>
                  <Card variant="gray" space={4}>
                    <Stack space={4} orientation="horizontal" align="center">
                      <IconList color="elementPurple60" />
                      <Text text={t('noOptions')} color="textSecondary" font="semibold14" />
                    </Stack>
                  </Card>
                  {noOptionsError && <FormError text={t('optionsError')} />}
                </Stack>
              ) : isSyncedList ? (
                <Stack space={2}>
                  {options.map((option) => (
                    <Card key={option.shortId} variant="transparent" space={4}>
                      <Stack space={1}>
                        <Text
                          text={option.label || 'No label'}
                          font="regular14"
                          color={option.label ? 'textPrimary' : 'textHelper'}
                          style={{ wordBreak: 'break-word' }}
                        />
                        <Text
                          text={option.value || 'No value'}
                          font="regular14"
                          color={option.value ? 'textPrimary' : 'textHelper'}
                          style={{ wordBreak: 'break-word' }}
                        />
                      </Stack>
                    </Card>
                  ))}
                </Stack>
              ) : (
                <Stack space={2}>
                  <SortableList
                    onChange={changeOrder}
                    items={options}
                    idProperty="shortId"
                    DraggableComponent={OptionDraggableRow}
                    componentProps={{
                      onEditItem: editOption,
                      onRemoveItem: removeOption,
                      isDuplicateValue,
                    }}
                  />
                </Stack>
              )}
              {!isSyncedList && (
                <Stack align="end">
                  <Button
                    variant="secondary"
                    iconLeft={<IconAddPlus />}
                    text={t('addOption')}
                    onClick={handleAddOption}
                  />
                </Stack>
              )}
            </Stack>
          </Container>
          <div id="list-form-actions" />
        </Stack>
      </Sidebar.Content>
    </Sidebar>
  );
};

type SyncedListDetailsProps = {
  syncData: SyncListType;
  onPatchSyncList: (id: string, data: Partial<SyncListType>) => void;
};

const SyncedListDetails = ({ syncData, onPatchSyncList }: SyncedListDetailsProps) => {
  const { t } = useScopedTranslate(locale);
  return (
    <Card variant="transparent" space={4}>
      <Stack space={4}>
        <CardItem
          title={t('sourceIntegration')}
          text={syncData.integration.name}
          icon={
            syncData.integration.thumbnail ? (
              <SquareImage src={syncData.integration.thumbnail} />
            ) : (
              <IconIntegration />
            )
          }
        />
        <CardItem title={t('url')} text={syncData.endpointUrl} icon={<IconUrl />} />
        <Stack space={4} orientation="horizontal" align="center" justify="space-between">
          <Stack space={4} orientation="horizontal" align="center">
            <IconFancyContainer icon={<IconCloudSync />} variant="purple" />
            <div>
              <Text text={t('autoSync')} color="textSecondary" font="semibold14" />
              <Text text={syncData.isActive ? t('enabled') : t('disabled')} font="regular14" />
            </div>
          </Stack>
          <Switch
            label={t('autoSync')}
            labelVisible={false}
            defaultValue={syncData.isActive}
            onChange={(isActive) => onPatchSyncList(syncData.shortId, { isActive })}
          />
        </Stack>
        {syncData.lastSync && (
          <CardItem
            title={t('lastSync')}
            text={abbreviateDate(syncData.lastSync, 'en', 'datetime')}
            icon={<IconDate />}
          />
        )}
        {syncData.conditions?.conditions && syncData.conditions.conditions.length > 0 && (
          <CardItem
            title={t('conditions')}
            text={t('conditionsCount', {
              count: syncData.conditions.conditions.length,
            })}
            icon={<IconFilter />}
          />
        )}
        {syncData.hasError && (
          <CardItem
            title={t('error')}
            text={`${t('syncFailed')} • ${syncData.errorMessage}`}
            icon={<IconCloudUnsync />}
            isError
          />
        )}
      </Stack>
    </Card>
  );
};

type CardItemProps = {
  title: string;
  text: string;
  icon: ReactElement<IconProps>;
  isError?: boolean;
};

const CardItem = ({ title, text, icon, isError }: CardItemProps) => {
  return (
    <Stack space={4} orientation="horizontal">
      <IconFancyContainer icon={icon} variant={isError ? 'red' : 'purple'} />
      <div>
        <Text text={title} font="semibold14" color={isError ? 'elementRed200' : 'textSecondary'} />
        <Text
          text={text}
          font="regular14"
          color={isError ? 'elementRed200' : 'textSecondary'}
          style={{ wordBreak: 'break-word' }}
        />
      </div>
    </Stack>
  );
};

const StyledDragIcon = styled(Stack)({
  width: pxToRem(40),
  minWidth: pxToRem(40),
});

const StyledCard = styled(Card)({
  border: `${pxToRem(2)} solid`,
  borderColor: `${theme.color.elementBlue100} !important`,
  background: `${theme.color.uiTrans5} !important`,
});

type OptionDraggableRowProps = {
  item: ListOption;
  attributes: any;
  listeners: any;
  onEditItem: (updated: ListOption) => void;
  onRemoveItem: (item: ListOption) => void;
  isDuplicateValue: (value: string, shortId: string) => boolean;
};

const OptionDraggableRow = ({
  item,
  attributes,
  listeners,
  onEditItem,
  onRemoveItem,
  isDuplicateValue,
}: OptionDraggableRowProps) => {
  const { t } = useScopedTranslate(locale);
  const isNew = !!item.isNew;
  const [editing, setEditing] = useState<boolean>(isNew);
  const [label, setLabel] = useState<string>(item.label ?? '');
  const [value, setValue] = useState<string>(item.value ?? '');
  const optionFormStore = useCreateFormStore();
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});

  const save = () => {
    const { isNew, ...rest } = item;
    onEditItem({ ...rest, label, value });
    setEditing(false);
  };

  const cancel = () => {
    optionFormStore.actions.resetStore();
    setFormErrors({});

    if (isNew) {
      onRemoveItem(item);
    } else {
      setLabel(item.label ?? '');
      setValue(item.value ?? '');
    }
    setEditing(false);
  };

  if (editing) {
    return (
      <StyledCard space={2}>
        <OptionForm
          store={optionFormStore}
          onSubmit={() => {
            if (label.trim() === '' || value.trim() === '') return false;
            const errors: Record<string, string> = {};
            if (isDuplicateValue(value, item.shortId)) {
              errors.value = 'Duplicate value';
            }
            setFormErrors(errors);
            if (Object.keys(errors).length > 0) return false;

            save();
            return true;
          }}
        >
          <Stack space={2}>
            <OptionForm.TextField
              id="label"
              label="Label"
              hideLabel
              placeholder="Add label"
              defaultValue={label}
              autoFocus
              onChange={setLabel}
              validators={['required']}
              maxChars={255}
            />
            <OptionForm.TextField
              id="value"
              label="Value"
              hideLabel
              placeholder="Add value"
              defaultValue={value}
              onChange={(newValue) => {
                setValue(newValue);
                if (formErrors.value) {
                  setFormErrors((prev) => ({ ...prev, value: '' }));
                }
              }}
              validators={['required']}
              maxChars={255}
            />
            {formErrors.value && <FormError text={formErrors.value} />}
            <OptionForm.Actions>
              <Stack orientation="horizontal" justify="end" space={2}>
                <Button variant="secondary" text={t('cancel')} onClick={cancel} />
                <OptionForm.Submit
                  variant="primary-blue"
                  text={isNew ? t('add') : t('update')}
                  iconLeft={<IconReturn />}
                />
              </Stack>
            </OptionForm.Actions>
          </Stack>
        </OptionForm>
      </StyledCard>
    );
  }

  return (
    <Card space={2} interactive {...attributes} {...listeners} style={{ cursor: 'grab' }}>
      <Stack space={4} orientation="horizontal" align="center" justify="space-between">
        <Stack orientation="horizontal" align="center" space={1}>
          <StyledDragIcon align="center" justify="center">
            <IconDrag color="elementPurple50" />
          </StyledDragIcon>
          <Stack space={1}>
            <Stack orientation="horizontal" space={1} align="center">
              {Boolean(item.label) ? (
                <Text text={item.label} font="regular14" style={{ wordBreak: 'break-word' }} />
              ) : (
                <Text text="No label" font="regular14" color="textHelper" />
              )}
            </Stack>
            <Stack orientation="horizontal" space={1} align="center">
              {Boolean(item.value) ? (
                <Text text={item.value} font="regular14" style={{ wordBreak: 'break-word' }} />
              ) : (
                <Text text="No value" font="regular14" color="textHelper" />
              )}
            </Stack>
          </Stack>
        </Stack>
        <Stack orientation="horizontal" align="center" space={1}>
          <IconButton
            variant="ghost-purple"
            icon={<IconEdit />}
            dataTestid="edit-option"
            onClick={() => setEditing(true)}
          />
          <IconButton
            variant="ghost-purple"
            icon={<IconRemove />}
            dataTestid="remove-option"
            onClick={() => onRemoveItem(item)}
          />
        </Stack>
      </Stack>
    </Card>
  );
};

const Container = styled.div({
  display: 'flex',
  justifyContent: 'space-between',
  '>div:nth-child(2)': {
    width: pxToRem(424),
  },
});

export const useListSidebar = () => {
  return useSidebar(ListSidebar, {
    variant: 'CONTENT_HEAVY',
    closeOnClickOutside: true,
  });
};

const locale = {
  en: {
    details: 'Details',
    name: 'List name',
    source: 'Source',
    title: 'Add list',
    titleEdit: 'Edit list',
    options: 'Options',
    addOption: 'Add option',
    noOptions: 'No options added',
    optionsError: 'Please add options',
    cancel: 'Cancel',
    save: 'Save',
    sourceIntegration: 'Source integration',
    url: 'URL',
    autoSync: 'Automatic sync',
    lastSync: 'Last sync',
    enabled: 'Enabled',
    disabled: 'Disabled',
    resync: 'Re-sync list',
    error: 'Error',
    syncFailed: 'Last sync failed',
    edit: 'Edit',
    add: 'Add',
    update: 'Update',
    conditions: 'Conditions',
    conditionsCount: '{count} condition(s) applied',
  },
};
